import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import Icon, { IconName } from './Icon';

import type { Profile } from '../../interfaces/profile/profile.interface';
import { HR_PRIVILEGED_ROLES } from '../../contexts/Auth.context';

interface NavEntry {
  key: string;
  label: string;
  icon: IconName;
  to: string;
  badge?: number;
}

// Three nav groups, each rendered under an uppercase eyebrow section label.
const WORKSPACE: NavEntry[] = [
  { key: 'dashboard', label: 'Dashboard', icon: 'dashboard', to: '/dashboard' }
];

const DEAL_FLOW: NavEntry[] = [
  { key: 'mandates', label: 'Mandates', icon: 'briefcase', to: '/mandates' },
  { key: 'clients', label: 'Clients', icon: 'users', to: '/clients' },
  { key: 'tombstones', label: 'Tombstones', icon: 'award', to: '/tombstones' }
];

const FIRM: NavEntry[] = [
  { key: 'documents', label: 'Documents', icon: 'folder', to: '/documents' },
  { key: 'employees', label: 'Employees', icon: 'user', to: '/employees' }
];

// HR phases 1-2.  Self-service for everyone — the API scopes each list
// to the current user unless they hold an HR role — so they are not gated.
const HR: NavEntry[] = [
  { key: 'timesheets', label: 'Timesheets', icon: 'clock', to: '/timesheets' },
  { key: 'expenses', label: 'Expense claims', icon: 'mail', to: '/expense-claims' },
  { key: 'payslips', label: 'Payslips', icon: 'doc', to: '/payslips' },
  { key: 'irp5s', label: 'IRP5s', icon: 'doc', to: '/irp5s' },
  { key: 'reviews', label: 'Performance', icon: 'award', to: '/performance-reviews' }
];

// Recruitment is HR-only — there is no self-service view of a hiring
// pipeline — so this is appended to the HR section only for those roles.
const HR_ONLY: NavEntry[] = [
  { key: 'prospects', label: 'Prospects', icon: 'users', to: '/prospects' }
];

// Bridge to the legacy portal for surfaces not yet re-skinned in v3 (HR,
// funds, events, contacts, precedents, admin).  /dashboard-legacy renders the
// old AuthenticatedLayout, whose sidebar is the full-navigation gateway.
const MORE: NavEntry[] = [
  { key: 'legacy', label: 'Other modules', icon: 'more', to: '/dashboard-legacy' }
];

interface Props {
  profile?: Profile;
  badges?: Partial<Record<string, number>>;
}

const initials = (name?: string): string => {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
};

const NavItem = ({ entry, badge }: { entry: NavEntry; badge?: number }) => {
  const location = useLocation();
  // Active state is path-based.  We treat the new /dashboard as the
  // canonical dashboard while v3 is active; the existing /dashboard,
  // /mandates etc. still render their old views.
  const active = location.pathname === entry.to || location.pathname.startsWith(`${entry.to}/`);
  return (
    <NavLink to={entry.to} className={`sb-nav-item ${active ? 'active' : ''}`}>
      <Icon name={entry.icon} size={16} />
      <span>{entry.label}</span>
      {badge !== undefined && <span className="badge">{badge}</span>}
    </NavLink>
  );
};

const Section = ({
  title,
  items,
  badges
}: {
  title: string;
  items: NavEntry[];
  badges?: Props['badges'];
}) => (
  <>
    <div className="sb-section">{title}</div>
    <nav className="sb-nav">
      {items.map((n) => (
        <NavItem key={n.key} entry={n} badge={badges?.[n.key]} />
      ))}
    </nav>
  </>
);

const Sidebar = ({ profile, badges }: Props) => {
  const hrPrivileged = HR_PRIVILEGED_ROLES.includes(profile?.role ?? '');
  const hrItems = hrPrivileged ? [...HR, ...HR_ONLY] : HR;

  return (
    <aside className="sb">
      <div className="sb-brand">
        <div className="sb-mark">B</div>
        <div className="sb-wordmark">
          BSM
          <small>Portal · v3</small>
        </div>
      </div>

      {/* visual-only search affordance — command palette not yet wired */}
      <div className="sb-search">
        <Icon name="search" size={14} />
        <span>Search…</span>
        <kbd>⌘K</kbd>
      </div>

      <Section title="Workspace" items={WORKSPACE} badges={badges} />
      <Section title="Deal Flow" items={DEAL_FLOW} badges={badges} />
      <Section title="Firm" items={FIRM} badges={badges} />
      <Section title="Human Resources" items={hrItems} badges={badges} />
      <Section title="More" items={MORE} badges={badges} />

      <div className="sb-foot">
        <div className="sb-avatar">{initials(profile?.full_name)}</div>
        <div className="who">
          {profile?.full_name || 'Signed-in user'}
          <small>
            {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : ''}
          </small>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
